import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiX } from 'react-icons/fi';
import { governanceMembersApi, fileUploadApi } from '../../api/phpApi';
import './AdminManage.css';

export default function AdminGovernance() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    position: '',
    description: '',
    image: '',
    email: '',
    phone: '',
    member_type: ''
  });

  // Fetch governance members
  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const data = await governanceMembersApi.getAll();
      setMembers(data || []);
    } catch (err) {
      setError('Failed to load governance members: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const url = await fileUploadApi.uploadImage(file, 'images', 'governance');
      setFormData(prev => ({ ...prev, image: url }));
      setImagePreview(url);
    } catch (err) {
      setError('Failed to upload image: ' + err.message);
    }
  };

  // Handle add/edit submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await governanceMembersApi.update(editingId, formData);
      } else {
        await governanceMembersApi.create(formData);
      }
      resetForm();
      loadMembers();
    } catch (err) {
      setError('Failed to save member: ' + err.message);
    }
  };

  // Handle edit
  const handleEdit = (member) => {
    setFormData(member);
    setImagePreview(member.image);
    setEditingId(member.id);
    setIsFormOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this member?')) {
      try {
        await governanceMembersApi.delete(id);
        loadMembers();
      } catch (err) {
        setError('Failed to delete member: ' + err.message);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      full_name: '',
      position: '',
      description: '',
      image: '',
      email: '',
      phone: '',
      member_type: ''
    });
    setImagePreview('');
    setEditingId(null);
    setIsFormOpen(false);
  };

  if (loading) return <div className="admin-loading">Loading governance members...</div>;

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Manage Governance Members</h2>
        <button 
          className="btn-primary btn-add"
          onClick={() => setIsFormOpen(true)}
        >
          <FiPlus /> Add Member
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingId ? 'Edit Member' : 'Add New Member'}</h3>
              <button className="btn-close" onClick={resetForm}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group form-row full">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Position *</label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Member Type</label>
                  <select
                    name="member_type"
                    value={formData.member_type}
                    onChange={handleInputChange}
                  >
                    <option value="">Select type</option>
                    <option value="Founder">Founder</option>
                    <option value="Director">Director</option>
                    <option value="Board Member">Board Member</option>
                    <option value="Advisor">Advisor</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group form-row full">
                <label>Bio/Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>Profile Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                {imagePreview && (
                  <img src={imagePreview} alt="Image Preview" className="image-preview" />
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <FiSave /> Save Member
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Members Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginTop: '20px' }}>
        {members.length === 0 ? (
          <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#6b7280' }}>No members found</p>
        ) : (
          members.map(member => (
            <div key={member.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', backgroundColor: 'white' }}>
              {member.image && (
                <img src={member.image} alt={member.full_name} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
              )}
              <div style={{ padding: '15px' }}>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem' }}>{member.full_name}</h4>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#0A66C2', fontWeight: '500' }}>{member.position}</p>
                {member.member_type && (
                  <span style={{ backgroundColor: '#f3f4f6', padding: '4px 12px', borderRadius: '4px', fontSize: '0.8rem' }}>{member.member_type}</span>
                )}
                {member.email && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>📧 {member.email}</p>
                )}
                <div className="actions" style={{ marginTop: '12px' }}>
                  <button
                    className="btn-icon btn-edit"
                    onClick={() => handleEdit(member)}
                    title="Edit"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => handleDelete(member.id)}
                    title="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
